import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, Typography } from "antd";
import { TUser } from "../../types/config";
import EncryptUtils from "../../utils/encrypt";
import useFetch from "../../hooks/useFetch";
import useToast from "../../hooks/useToast";
import { eDocType, eLANGUAGE, eResultCode } from "../../utils/enum";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import { CloseOutlined, CheckOutlined } from "@ant-design/icons";
import styles from "./auth.module.css";
import { SignUp, UploadDocs } from "../../utils/api.constant";
import AuthUtil from "../../utils/auth";
import Text, { LanguageContext } from "../../context/language-context";
import { LoaderContext } from "../../context/loader";
import {
  allowOnlyNumericInput_onKeyDown,
  disableFutureDates_on_input_date_max,
  validateFile,
} from "../../utils/input.validator";
import { Regex } from "../../utils/validation";
import Utils from "../../utils";

const { Option } = Select;

const formItemLayout = {
  labelCol: {
    span: 24, // Ensures label spans the full width above the input
  },
  wrapperCol: {
    span: 24, // Ensures input also spans the full width below the label
  },
};

interface RegisterProps {
  setAutoEmailorPhone: (emailorPhone: string) => void;
  setAutoPassword: (password: string) => void;
}

const Register: React.FC<RegisterProps> = ({
  setAutoPassword,
  setAutoEmailorPhone,
}) => {
  const [form] = Form.useForm();
  // Hook for making API requests
  const { post } = useFetch();
  // Hook for displaying toasts
  const { onShowToast } = useToast();
  const [aadharFile, setAadharFile] = useState<File | null>(null);
  const [aadharId, setAadharId] = useState<string>("");
  const [passportFile, setPassportFile] = useState<File | null>(null);
  const [passportFileId, setPassportFileId] = useState<string>("");
  const { userLanguage, userLanguageChange } = useContext(LanguageContext);

  // Loader Context
  const contextForLoader = useContext(LoaderContext);
  // const { isLoading, setIsLoading } = contextForLoader as any;
  const [isLoading, setIsLoading] = useState<boolean>(false);



  const onFinish = async (values: TUser) => {
    setIsLoading(true);
    try {
      const payload: { data: TUser } = {
        data: {
          fullName: values.fullName,
          fatherName: values.fatherName,
          dateOfBirth: values.dateOfBirth,
          mobileNumber: values.mobileNumber,
          aadhaarCardNumber: values.aadhaarCardNumber,
          emailId: values.emailId.toLowerCase(),
          language: values.language,
          password: EncryptUtils.encrypt(values.password as string),
          preferredLanguage: values.preferredLanguage,
        },
      };
      if (aadharId || passportFileId) {
        payload.data.docIds = [];
        if (aadharId) {
          payload.data.docIds.push(parseInt(aadharId));
        }
        if (passportFileId) {
          payload.data.docIds.push(parseInt(passportFileId));
        }
      }

      const response = await post(SignUp, payload);
      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode == eResultCode.SUCCESS) {
        setIsLoading(false);
        setAutoEmailorPhone(values.emailId.toLowerCase() as string);
        setAutoPassword(values.password as string);
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content:
            userLanguage === "hi"
              ? "आप सफलतापूर्वक पंजीकृत कर लिए गए हैं।"
              : userLanguage === "sk"
          ? "भवान् साफल्यरूपेण पञ्जीकृतः।"
          : "You have been successfully registered",
        });
        if (values.preferredLanguage) {
          userLanguageChange(
            values.preferredLanguage === eLANGUAGE.ENGLISH
              ? "en"
              : values.preferredLanguage === eLANGUAGE.HINDI
                ? "hi"
                : "sk"
          );
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: description
        });
      }
    } catch (error) {
      console.error("Registration error", error);
    } finally {
      form.resetFields();
      setIsLoading(false);
      setPassportFile(null);
      setAadharId("");
      setAadharFile(null);
      setPassportFileId("");
      form.setFieldValue("aadhaarCardImage", "");
      form.setFieldValue("passportPhoto", "");
    }
  };

  const uploadDocument = async (file: File, type: eDocType) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", type.toString());
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        }
      );
      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode, description } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        if (type === eDocType.AADHAR) {
          setAadharId(data?.id.toString());
        } else if (type === eDocType.PASSPORTSIZEPHOTO) {
          setPassportFileId(data?.id?.toString());
        }
      } else {
        console.error("Failed to upload images", response.statusText);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (aadharFile) {
      uploadDocument(aadharFile, eDocType.AADHAR);
    }
  }, [aadharFile]);

  useEffect(() => {
    if (passportFile) {
      uploadDocument(passportFile, eDocType.PASSPORTSIZEPHOTO);
    }
  }, [passportFile]);

  const prefixSelector = (
    <Form.Item name="prefix" noStyle>
      <Select style={{ width: 70 }}>
        <Option value="86">+91</Option>
        {/* <Option value="87">+87</Option> */}
      </Select>
    </Form.Item>
  );

  const [autoCompleteResult, setAutoCompleteResult] = useState<string[]>([]);

  const PasswordsDontMatch = Text({
    tid: "passwords_do_not_match",
    def: "The two passwords that you entered do not match!",
  });

  return (
    <div className={styles.parentWrapper}>
      <Form
        {...formItemLayout}
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={{}}
        scrollToFirstError
        layout="vertical" // This is the key setting to make labels appear on top
      >
        <Row>
          <Typography.Title level={3}>
            {Text({ tid: "sign_up", def: "Sign up" })}
          </Typography.Title>
          <Typography.Text type="danger">
            {Text({
              tid: "all_fields_are_mandatory",
              def: "All fields are mandatory!",
            })}
          </Typography.Text>
        </Row>

        <Row gutter={30}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="fullName"
              label={Text({ tid: "full_name", def: "Full Name" })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_input_full_name",
                    def: "Please input your full name!",
                  }),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="fatherName"
              label={Text({ tid: "fathers_name", def: "Father's Name" })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_input_fathers_name",
                    def: "Please input your father's name!",
                  }),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={30}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="dateOfBirth"
              label={Text({ tid: "date_of_birth", def: "Date of Birth" })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_input_date_of_birth",
                    def: "Please input your date of birth!",
                  }),
                },
              ]}
            >
              <Input type="date" max={disableFutureDates_on_input_date_max()} />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="mobileNumber"
              label={Text({ tid: "mobile_number", def: "Mobile Number" })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: Regex.MOBILENO, // Updated pattern
                  message: Text({
                    tid: "mobile_number_validation",
                    def: "Enter a valid 10-digit mobile number",
                  }),
                },
              ]}
            >
              <Input
                maxLength={10}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={30}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="aadhaarCardNumber"
              label={Text({
                tid: "aadhaar_card_number",
                def: "Aadhaar Card Number",
              })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_input_aadhaar_card_number",
                    def: "Please input your aadhaar card number!",
                  }),
                },
                {
                  pattern: /^[2-9]{1}[0-9]{11}$/,
                  message: Text({
                    tid: "aadhaar_number_must_be_12_digits",
                    def: "Aadhaar number must be 12 digits!",
                  }),
                },
              ]}
            >
              <Input
                maxLength={12}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            {/* <span className="test"> */}
            <Form.Item
              name="aadhaarCardImage"
              label={Text({
                tid: "upload_aadhaar_card",
                def: "Upload Aadhaar Card",
              })}
            >
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!validateFile(file as File).valid) {
                    onShowToast({
                      type: ToastType.error,
                      title: <CloseOutlined />,
                      position: ToastOpen.leftBottom,
                      content: validateFile(file as File).error,
                    });
                    e.target.value = "";
                    form.resetFields(["aadhaarCardImage"]);
                  } else if (file) {
                    setAadharFile(file);
                  }
                }}
              />
            </Form.Item>
            {/* </span> */}
          </Col>
        </Row>

        <Row gutter={30}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="passportPhoto"
              label={Text({
                tid: "upload_passport_photo",
                def: "Upload Passport Photo",
              })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "PLEASE_UPLOAD_PASSPORT_PHOTO",
                    def: "Please upload your passport photo!",
                  }),
                },
              ]}
            >
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!validateFile(file as File).valid) {
                    onShowToast({
                      type: ToastType.error,
                      title: <CloseOutlined />,
                      position: ToastOpen.leftBottom,
                      content: validateFile(file as File).error,
                    });
                    // e.target.value = "";
                    form.resetFields(["passportPhoto"]);
                  } else if (file) {
                    setPassportFile(file);
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="emailId"
              label={Text({ tid: "email_address", def: "Email Address" })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  type: "email",
                  message: Text({
                    tid: "VALID_EMAIL",
                    def: "VALID_EMAIL",
                  }),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={30}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="password"
              label={Text({ tid: "password", def: "Password" })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_input_password",
                    def: "Please input your password!",
                  }),
                },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                  message: Text({
                    tid: "PASSWORD_REQUIREMENTS",
                    def: "Password must be at least 8 characters long, include uppercase, lowercase, number, and special character!",
                  }),
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name="confirmPassword"
              label={Text({ tid: "confirm_password", def: "Confirm Password" })}
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_input_password",
                    def: "Please confirm your password!",
                  }),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(PasswordsDontMatch));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={30}>
          <Col xs={24}>
            <Form.Item
              name="preferredLanguage"
              label={Text({
                tid: "preferred_language",
                def: "Preferred Language",
              })}
              rules={[
                {
                  required: true,
                  message: Text({
                    tid: "please_select_preferred_language",
                    def: "Please select your Preferred language!",
                  }),
                },
              ]}
            >
              <Select
                placeholder={Text({ tid: "select", def: "Select" })}
                options={Utils.getEnumOptions(eLANGUAGE).map((option) => ({
                  ...option,
                  label:
                    option.value === eLANGUAGE.ENGLISH
                      ? "English"
                      : option.value === eLANGUAGE.HINDI
                      ? "हिन्दी"
                      : option.value === eLANGUAGE.SANSKRIT
                      ? "संस्कृतम्"
                      : option.label,
                }))}
                onChange={(option) => {
                  localStorage.setItem(
                    "rcml-lang",
                    option === eLANGUAGE.ENGLISH
                      ? "en"
                      : option === eLANGUAGE.HINDI
                      ? "hi"
                      : "sk"
                  );
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className="mt-15">
          <Button
          className="w-full"
            type="primary"
            htmlType="submit"
            disabled={isLoading}
            loading={isLoading}
          >
            {Text({ tid: "create_account", def: "Create Account" })}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Register;
